import { useCallback, useEffect, useState } from "react";
import {
  createManufacturingRecipe,
  deleteManufacturingRecipe,
  getManufacturingRecipeDetail,
  listManufacturingRecipes,
  setManufacturingRecipeLines,
  updateManufacturingRecipe,
  type MfgRecipeDetailResponse,
  type MfgRecipeKind,
  type MfgRecipeSummary,
} from "@/lib/manufacturingClient";
import {
  duplicateLinesToSetPayload,
  duplicateRecipeDraftFromDetail,
  type DuplicateRecipeDraft,
} from "@/lib/manufacturing/duplicateRecipeDraft";
import {
  isDuplicateIdentityUnchanged,
  type DuplicateFormPatch,
} from "@/lib/manufacturing/duplicateRecipeDraftBridge";
import {
  formatRecipeTagsInput,
  parseRecipeTagsInput,
} from "@/lib/manufacturing/recipeTagsInput";
import { isImpliedVariantTag, mergeImpliedRulesIntoTags } from "@/lib/manufacturing/impliedVariantTag";
import type { ImpliedVariantRule } from "@/lib/manufacturing/impliedVariantTag";
import {
  formatShelfPriceInput,
  parseShelfPriceInput,
  recipeKindHasShelfPrice,
} from "@/lib/manufacturing/shelfPrice";
import {
  mergeVariantConfigIntoTags,
  parseVariantConfigFromTags,
  visibleRecipeTags,
} from "@/lib/manufacturing/variantConfig";

export function useManufacturingModelsTab(modelsNavTick: number, modelsNewSignal: number) {
  const [recipes, setRecipes] = useState<MfgRecipeSummary[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MfgRecipeDetailResponse | null>(null);
  const [duplicateDraft, setDuplicateDraft] = useState<DuplicateRecipeDraft | null>(null);

  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newKind, setNewKind] = useState<MfgRecipeKind>("assembly");
  const [newTagsInput, setNewTagsInput] = useState("");
  const [newRecipeGroupId, setNewRecipeGroupId] = useState("");

  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editKind, setEditKind] = useState<MfgRecipeKind>("assembly");
  const [editTagsInput, setEditTagsInput] = useState("");
  const [editRecipeGroupId, setEditRecipeGroupId] = useState("");
  const [editWholesale, setEditWholesale] = useState("");
  const [editMsrp, setEditMsrp] = useState("");

  const loadRecipes = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const res = await listManufacturingRecipes();
    setListBusy(false);
    if (!res.ok) {
      setRecipes([]);
      setLocalError(res.error);
      return;
    }
    setRecipes(Array.isArray(res.data.recipes) ? res.data.recipes : []);
  }, []);

  const loadDetail = useCallback(async (recipeId: string) => {
    setDetailBusy(true);
    setLocalError(null);
    const res = await getManufacturingRecipeDetail(recipeId);
    setDetailBusy(false);
    if (!res.ok) {
      setDetail(null);
      setLocalError(res.error);
      return;
    }
    setDetail(res.data);
    setEditCode(res.data.recipe.code);
    setEditName(res.data.recipe.name);
    setEditNotes(res.data.recipe.notes ?? "");
    setEditKind(res.data.recipe.kind ?? "assembly");
    setEditRecipeGroupId(res.data.recipe.recipe_group_id ?? "");
    setEditWholesale(formatShelfPriceInput(res.data.recipe.current_wholesale_usd));
    setEditMsrp(formatShelfPriceInput(res.data.recipe.current_msrp_usd));
    setEditTagsInput(
      Array.isArray(res.data.recipe.tags)
        ? formatRecipeTagsInput(visibleRecipeTags(res.data.recipe.tags))
        : "",
    );
  }, []);

  useEffect(() => {
    void loadRecipes();
  }, [loadRecipes, modelsNavTick]);

  useEffect(() => {
    if (modelsNewSignal === 0) return;
    setCreating(true);
    setSelectedId(null);
    setDetail(null);
    setDuplicateDraft(null);
    setNewCode("");
    setNewName("");
    setNewNotes("");
    setNewKind("assembly");
    setNewTagsInput("");
    setNewRecipeGroupId("");
    setLocalError(null);
  }, [modelsNewSignal]);

  useEffect(() => {
    if (!selectedId || creating) return;
    void loadDetail(selectedId);
  }, [selectedId, creating, modelsNavTick, loadDetail]);

  function pickRecipe(id: string) {
    setCreating(false);
    setDuplicateDraft(null);
    setSelectedId(id);
  }

  function closeSelectedModel() {
    setCreating(false);
    setSelectedId(null);
    setDetail(null);
    setDuplicateDraft(null);
  }

  function resetNewModelFields() {
    setNewCode("");
    setNewName("");
    setNewNotes("");
    setNewKind("assembly");
    setNewTagsInput("");
    setNewRecipeGroupId("");
    setDuplicateDraft(null);
  }

  const beginDuplicateRecipe = useCallback(async (recipeId: string) => {
    setSaveBusy(true);
    setLocalError(null);
    const res = await getManufacturingRecipeDetail(recipeId);
    setSaveBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    const draft = duplicateRecipeDraftFromDetail(res.data);
    setCreating(true);
    setSelectedId(null);
    setDetail(null);
    setDuplicateDraft(draft);
    setNewCode(draft.sourceCode);
    setNewName(draft.sourceName);
    setNewNotes(draft.notes);
    setNewKind(draft.kind);
    setNewTagsInput(formatRecipeTagsInput(visibleRecipeTags(draft.tags)));
    setNewRecipeGroupId(res.data.recipe.recipe_group_id ?? "");
    setLocalError(null);
  }, []);

  function applySmartDuplicatePatch(patch: DuplicateFormPatch) {
    setNewCode(patch.code);
    setNewName(patch.name);
    setNewKind(patch.kind);
    setNewTagsInput(patch.tagsInput);
    setDuplicateDraft(patch.duplicate);
    setLocalError(null);
  }

  async function onCreateRecipe() {
    const code = newCode.trim();
    const name = newName.trim();
    if (!code || !name) {
      setLocalError("Code and name are required.");
      return;
    }
    if (duplicateDraft && isDuplicateIdentityUnchanged(duplicateDraft, code, name)) {
      setLocalError(
        "Change the code and name so they differ from the source model before saving.",
      );
      return;
    }
    setSaveBusy(true);
    setLocalError(null);
    const typedTags = parseRecipeTagsInput(newTagsInput).filter((tag) => !isImpliedVariantTag(tag));
    const implyTags = duplicateDraft?.tags.filter((tag) => isImpliedVariantTag(tag)) ?? [];
    const tags = [...typedTags, ...implyTags];
    const res = await createManufacturingRecipe({
      code,
      name,
      notes: newNotes.trim() || undefined,
      kind: newKind,
      tags: tags.length > 0 ? tags : undefined,
      recipe_group_id: newRecipeGroupId.trim() || null,
    });
    if (!res.ok) {
      setSaveBusy(false);
      setLocalError(res.error);
      return;
    }

    const newId = res.data.recipe.id;
    const lines = duplicateDraft ? duplicateLinesToSetPayload(duplicateDraft.lines) : [];
    if (lines.length > 0) {
      const linesRes = await setManufacturingRecipeLines({ recipe_id: newId, lines });
      if (!linesRes.ok) {
        setSaveBusy(false);
        setLocalError(
          `Model created, but BOM lines could not be copied: ${linesRes.error}`,
        );
        setCreating(false);
        setDuplicateDraft(null);
        setSelectedId(newId);
        await loadRecipes();
        return;
      }
    }

    setSaveBusy(false);
    setCreating(false);
    setDuplicateDraft(null);
    setSelectedId(newId);
    await loadRecipes();
  }

  async function onSaveRecipeMeta(canEditCode: boolean, implyRules: readonly ImpliedVariantRule[]) {
    if (!selectedId) return;
    const code = editCode.trim();
    if (canEditCode && !code) {
      setLocalError("Recipe code cannot be blank.");
      return;
    }
    setSaveBusy(true);
    setLocalError(null);
    const tags = mergeVariantConfigIntoTags(
      mergeImpliedRulesIntoTags(
        parseRecipeTagsInput(editTagsInput).filter((tag) => !isImpliedVariantTag(tag)),
        implyRules,
      ),
      parseVariantConfigFromTags(detail?.recipe.tags ?? []),
    );
    const shelf = recipeKindHasShelfPrice(editKind)
      ? {
          wholesale: parseShelfPriceInput(editWholesale, "Wholesale"),
          msrp: parseShelfPriceInput(editMsrp, "MSRP"),
        }
      : null;
    if (shelf && !shelf.wholesale.ok) {
      setSaveBusy(false);
      setLocalError(shelf.wholesale.error);
      return;
    }
    if (shelf && !shelf.msrp.ok) {
      setSaveBusy(false);
      setLocalError(shelf.msrp.error);
      return;
    }
    const res = await updateManufacturingRecipe({
      recipe_id: selectedId,
      ...(canEditCode ? { code } : {}),
      name: editName.trim() || undefined,
      notes: editNotes,
      kind: editKind,
      tags,
      recipe_group_id: editRecipeGroupId.trim() || null,
      ...(shelf && shelf.wholesale.ok && shelf.msrp.ok
        ? { current_wholesale_usd: shelf.wholesale.value, current_msrp_usd: shelf.msrp.value }
        : {}),
    });
    setSaveBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    await loadRecipes();
    await loadDetail(selectedId);
  }

  async function onDeactivateRecipe() {
    if (!selectedId) return { ok: false as const, error: "No model selected." };
    setSaveBusy(true);
    setLocalError(null);
    const res = await updateManufacturingRecipe({ recipe_id: selectedId, is_active: false });
    setSaveBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return { ok: false as const, error: res.error };
    }
    setSelectedId(null);
    setDetail(null);
    await loadRecipes();
    return { ok: true as const };
  }

  async function onPermanentlyDeleteRecipe(recipeId: string) {
    setSaveBusy(true);
    setLocalError(null);
    const res = await deleteManufacturingRecipe({ recipe_id: recipeId });
    setSaveBusy(false);
    if (!res.ok) {
      const blockers =
        res.body &&
        typeof res.body === "object" &&
        res.body !== null &&
        "blockers" in res.body &&
        Array.isArray((res.body as { blockers: unknown }).blockers)
          ? ((res.body as { blockers: Array<{ code: string; message: string }> }).blockers ?? [])
          : [];
      return { ok: false as const, error: res.error, blockers };
    }
    if (selectedId === recipeId) {
      setSelectedId(null);
      setDetail(null);
    }
    await loadRecipes();
    return { ok: true as const };
  }

  const reloadDetail = useCallback(() => {
    if (selectedId) void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  return {
    recipes,
    listBusy,
    detailBusy,
    saveBusy,
    localError,
    setLocalError,
    creating,
    selectedId,
    detail,
    duplicateDraft,
    newCode,
    setNewCode,
    newName,
    setNewName,
    newNotes,
    setNewNotes,
    newKind,
    setNewKind,
    newTagsInput,
    setNewTagsInput,
    newRecipeGroupId,
    setNewRecipeGroupId,
    editCode,
    setEditCode,
    editName,
    setEditName,
    editNotes,
    setEditNotes,
    editKind,
    setEditKind,
    editTagsInput,
    setEditTagsInput,
    editRecipeGroupId,
    setEditRecipeGroupId,
    editWholesale,
    setEditWholesale,
    editMsrp,
    setEditMsrp,
    pickRecipe,
    closeSelectedModel,
    beginDuplicateRecipe,
    applySmartDuplicatePatch,
    resetNewModelFields,
    onCreateRecipe,
    onSaveRecipeMeta,
    onDeactivateRecipe,
    onPermanentlyDeleteRecipe,
    reloadDetail,
  };
}
